export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  minQuality?: number;
  maxBytes?: number;
  targetType?: 'image/jpeg' | 'image/webp';
}

const DEFAULT_OPTIONS: Required<ImageCompressionOptions> = {
  maxWidth: 1600,
  maxHeight: 1600,
  quality: 0.82,
  minQuality: 0.62,
  maxBytes: 750 * 1024,
  targetType: 'image/jpeg',
};

const SKIPPED_IMAGE_TYPES = new Set(['image/gif', 'image/svg+xml']);

function isCompressibleImage(file: File): boolean {
  return Boolean(file.type?.startsWith('image/')) && !SKIPPED_IMAGE_TYPES.has(file.type);
}

function replaceExtension(filename: string, extension: string): string {
  const cleanName = String(filename || 'image').replace(/[/\\]/g, '-');
  const dot = cleanName.lastIndexOf('.');
  const base = dot > 0 ? cleanName.slice(0, dot) : cleanName;
  return `${base || 'image'}.${extension}`;
}

function blobToFile(blob: Blob, original: File): File {
  const extension = blob.type === 'image/webp' ? 'webp' : 'jpg';
  return new File([blob], replaceExtension(original.name, extension), {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  if (typeof canvas.toBlob === 'function') {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
  }

  try {
    const dataUrl = canvas.toDataURL(type, quality);
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/data:(.*?);base64/)?.[1] || type;
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return Promise.resolve(new Blob([bytes], { type: mime }));
  } catch {
    return Promise.resolve(null);
  }
}

async function loadImageElement(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image decode failed'));
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function decodeImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return loadImageElement(file);
}

export function getFileExtensionFromMime(type: string | undefined): string {
  if (type === 'image/webp') return 'webp';
  if (type === 'image/jpeg') return 'jpg';
  if (type === 'image/png') return 'png';
  if (type === 'image/gif') return 'gif';
  if (type === 'image/svg+xml') return 'svg';
  return 'bin';
}

export function withFileExtension(path: string, extension: string): string {
  const queryIndex = path.indexOf('?');
  const basePath = queryIndex >= 0 ? path.slice(0, queryIndex) : path;
  const query = queryIndex >= 0 ? path.slice(queryIndex) : '';
  const slashIndex = basePath.lastIndexOf('/');
  const dotIndex = basePath.lastIndexOf('.');
  const hasExtension = dotIndex > slashIndex;
  const nextBase = hasExtension ? basePath.slice(0, dotIndex) : basePath;
  return `${nextBase}.${extension}${query}`;
}

export async function compressImageFile(
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> {
  if (!isCompressibleImage(file)) return file;

  const settings = { ...DEFAULT_OPTIONS, ...options };
  let decoded: ImageBitmap | HTMLImageElement | null = null;

  try {
    decoded = await decodeImage(file);
    const sourceWidth = decoded.width;
    const sourceHeight = decoded.height;
    if (!sourceWidth || !sourceHeight) return file;

    const scale = Math.min(1, settings.maxWidth / sourceWidth, settings.maxHeight / sourceHeight);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return file;

    if (settings.targetType === 'image/jpeg') {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
    }
    context.drawImage(decoded, 0, 0, width, height);

    let quality = settings.quality;
    let bestBlob: Blob | null = null;

    while (quality >= settings.minQuality) {
      const blob = await canvasToBlob(canvas, settings.targetType, quality);
      if (!blob) break;
      bestBlob = blob;
      if (blob.size <= settings.maxBytes) break;
      quality -= 0.08;
    }

    if (!bestBlob) return file;
    if (bestBlob.size >= file.size && file.size <= settings.maxBytes) return file;

    return blobToFile(bestBlob, file);
  } catch (error) {
    console.warn('Image compression skipped:', error);
    return file;
  } finally {
    if (decoded && 'close' in decoded && typeof decoded.close === 'function') {
      decoded.close();
    }
  }
}
